import React, { ReactElement, SyntheticEvent, useState } from 'react';
import styles from './createPasswordPage.module.scss';
import { Title } from '../../../components/title/Title';
import { usePasswordResetVerifyToken } from '../hooks/useVerifyToken/useVerifyToken';
import LinkExpiredPage from '../linkExpired/LinkExpiredPage';
import { useHistory, useParams } from 'react-router-dom';
import PasswordInput from './passwordInput/PasswordInput';
import { TechnicalError } from '../../../components/technicalError/TechnicalError';
import { LoadableButton } from '@mo/loadable-button';
import Padlock from './../../../components/padlock/Padlock';
import { PageErrorBox } from '@mo/page-error-box';
import { PASSWORD_CHANGED_PAGE } from '../../../common/routes';
import { PageError } from '../../../domain/PageError';
import { updatePassword } from '../forgotPasswordGateway';
import {
    BLANK_OR_INVALID_PASSWORD,
    GENERIC_PASSWORD_ERROR,
    INVALID_CHARACTER,
    nonAsciiRegex,
    PASSWORD_TOO_LONG,
    passwordRegexp,
} from '../../../common/passwordValidation/validation';
import { PasswordValidationErrors } from '../../../common/passwordValidation/errors';

export interface ParamTypes {
    token: string;
    username: string;
}

const CreatePasswordPage: React.FunctionComponent = (): ReactElement | null => {
    const { token } = useParams<ParamTypes>();
    const { username } = useParams<ParamTypes>();
    const { loading, unauthorisedError } = usePasswordResetVerifyToken(token, username); 

    const [password, setPassword] = useState<string>('');
    const [technicalError, setTechnicalError] = useState<boolean>(false);
    const [passwordValidationError, setPasswordValidationError] = useState<string[]>([]);
    const [submissionLoading, setSubmissionLoading] = useState<boolean>(false);
    const history = useHistory();

    const handleSubmit = async (event: SyntheticEvent): Promise<void> => {
        event.preventDefault();

        if (password.length === 0) return setPasswordValidationError([BLANK_OR_INVALID_PASSWORD]);
        if (password.length > 64) return setPasswordValidationError([PASSWORD_TOO_LONG]);
        if (nonAsciiRegex.test(password)) return setPasswordValidationError([INVALID_CHARACTER]);
        if (!passwordRegexp.test(password)) return setPasswordValidationError([BLANK_OR_INVALID_PASSWORD]);

        try {
            setTechnicalError(false);
            setSubmissionLoading(true);

            await updatePassword(password, username);

            setSubmissionLoading(false);
            history.push(PASSWORD_CHANGED_PAGE);
        } catch (error) {
            setSubmissionLoading(false);
            if (error instanceof PasswordValidationErrors) {
                return setPasswordValidationError([...passwordValidationError, ...error.validationErrors]);
            } else {
                setTechnicalError(true);
            }
        }
    };

    const onPasswordChange = (newPassword: string): void => {
        setPasswordValidationError([]);
        setPassword(newPassword);
    };

    const mapPasswordValidationPageErrors = (validationErrors: string[]): PageError[] => {
        return validationErrors.map(
            (validationError: string): PageError => {
                return { label: 'Password', message: validationError };
            }
        );
    };

    const mapPasswordValidationInlineErrors = (validationErrors: string[]): string | undefined => {
        if (validationErrors.length > 1) return GENERIC_PASSWORD_ERROR;
        else if (validationErrors.length === 1) return validationErrors[0];
    };

    const errors = mapPasswordValidationPageErrors(passwordValidationError);

    const hasErrors = errors.length > 0;

    if (loading) {
        return null;
    }

    if (unauthorisedError) {
        return <LinkExpiredPage />;
    } else {
        return (
            <div className={styles.gridContainer}>
                <div className={styles.page}>
                    <div className={styles.form}>
                        <div className={styles.formHeader}>
                            <PageErrorBox className={styles.pageErrorBox} hasErrors={hasErrors} errors={errors} />
                            <div className={hasErrors ? styles.formHeaderContentInError : styles.formHeaderContent}>
                                <Title title='Create a new password' />
                                <p className={styles.instructionText}>
                                    {`Please create a secure password to sign in to Online Applications going forward.`}
                                </p>
                            </div>
                        </div>
                        <div className={styles.formBody}>
                            <div className={styles.passwordEntryContainer}>
                                <div className={styles.passwordEntryForm}>
                                    <form>
                                        <PasswordInput
                                            password={password}
                                            error={mapPasswordValidationInlineErrors(passwordValidationError)}
                                            onChange={onPasswordChange}
                                        />
                                        {technicalError && (
                                            <TechnicalError>
                                                Sorry, we're having some technical difficulties. Please try again 
                                                in a few moments. If the problem persist please contact your team 
                                                at Manufacturer Relations <a href='mailto:manufacturerrelations@motabilityoperations.co.uk'>manufacturerrelations@motabilityoperations.co.uk </a> 
                                                or DFW and adaptations <a href='mailto:dfwandadaptation@motabilityoperations.co.uk'>dfwandadaptation@motabilityoperations.co.uk</a>.
                                            </TechnicalError>
                                        )}
                                        <LoadableButton
                                            loading={submissionLoading}
                                            loadingText='Confirming'
                                            notLoadingText='Confirm'
                                            onClick={handleSubmit}
                                            className={styles.continueButton}
                                        />
                                    </form>
                                </div>
                                <Padlock className={styles.padlock} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
};

export default CreatePasswordPage;
